import {
  PHASE_BUILD,
  PHASE_COMBAT,
  PHASE_CINEMATIC,
  PHASE_DRAFT,
} from "../types.ts";
import {
  setPhase,
  setWave,
  setWaveActive,
  setWaveCountdown,
  setWaveStartedThisCountdown,
} from "../stateIntents.ts";
import type { Card } from "../../entities/types.ts";
import type { Vec2 } from "../../core/types.ts";
import type { BreachResult } from "../../systems/BreachSystem.ts";
import {
  applyCardEffect,
  removeCardEffect,
  isPayloadCard,
} from "../cardEffectHandlers.ts";
import { t, localizedName } from "../../utils/i18n.ts";
import { COLORS, THREAT_PULSE_DURATION } from "../../core/constants.ts";
import { CELL_SIZE, FINAL_WAVE, GAME } from "../../core/config.ts";
import { PRIORITY } from "../../ui/commsContent.ts";
import type { HandlerState, WaveHandlerDeps } from "./types.ts";

/** D-331 — a no-op fallback so a headless caller without `commsRouter`
 *  (e.g. a narrow test harness) never throws on the newly-rerouted status
 *  lines that used to be `gameBanner.show()` calls. */
function commsStatus(
  ctx: WaveHandlerDeps,
  text: string,
  priority: number,
): void {
  ctx.commsRouter?.status(text, priority);
}

/** Wire wave lifecycle EventBus subscriptions. */
export function setupWaveHandlers(
  ctx: WaveHandlerDeps,
  state: HandlerState,
): (() => void)[] {
  const {
    eventBus,
    gs,
    gridRenderer,
    particleSystem,
    draftSystem,
    draftUI,
    skillOverview,
    economy,
    combatSystem,
  } = ctx;
  const unsubs: (() => void)[] = [];

  // Track breach data from this wave cycle for spawn alert (CR2). BL-146: a
  // QUEUE — the Act III pincer erupts ≥2 spawns in one transition, so each
  // breach's spawn-alert + path-reveal is drained in turn before the countdown.
  let pendingBreaches: Array<{ path: Vec2[]; destroyedCells: Vec2[] }> = [];
  // Track PATH_RESERVED cells across the countdown so we can release on wave start
  let reservedCountdownCells: Vec2[] = [];

  unsubs.push(
    eventBus.on("wave.started", (d: unknown) => {
      const ev = d as { wave: number };
      ctx.grid.releaseReservedPath(reservedCountdownCells);
      reservedCountdownCells = [];
      setWave(gs, ev.wave);
      setWaveActive(gs, true);
      setPhase(gs, PHASE_COMBAT);
      // Bug 8 (cosmetic): duration-card expiry is handled in the post-wave cycle
      // (proceedPostWave), BEFORE the next draft, so the player sees the slot
      // free up first instead of a card seeming to "slide onto" an old slot on
      // the next wave start (it was always expiry+reindex, never a swap).
      if (ev.wave === 7) {
        ctx.gameBanner.show(t("stealth.warning"), COLORS.AMBER_WARN, 3000);
      }
      if (ev.wave === 1) {
        gridRenderer.triggerThreatPulse(THREAT_PULSE_DURATION);
        eventBus.emit("threat.pulse");
      }
    }),
  );

  // Re-reserve path when it's recalculated mid-countdown (off-path tower placement).
  // Without this, the new path's cells would be unlocked for further placement.
  unsubs.push(
    eventBus.on("path.recalculated", () => {
      if (gs.phase !== PHASE_BUILD || gs.waveCountdown <= 0) return;
      ctx.grid.releaseReservedPath(reservedCountdownCells);
      const pathGridCells = gs.pathPixels.map((p) => ({
        x: Math.floor(p.x / CELL_SIZE),
        y: Math.floor(p.y / CELL_SIZE),
      }));
      reservedCountdownCells = ctx.grid.reservePath(pathGridCells);
    }),
  );

  /** CR1: Start the pre-wave countdown (10s timer). */
  function startPreWaveCountdown(): void {
    setPhase(gs, PHASE_BUILD);
    setWaveCountdown(gs, gs.waveCountdownMax);
    setWaveStartedThisCountdown(gs, false);
    // Lock the freshly recomputed path so placement during the countdown window
    // can't silently invalidate the river animation the player just saw.
    const pathGridCells = gs.pathPixels.map((p) => ({
      x: Math.floor(p.x / CELL_SIZE),
      y: Math.floor(p.y / CELL_SIZE),
    }));
    reservedCountdownCells = ctx.grid.reservePath(pathGridCells);
  }

  /** CR2: Show spawn alert + path animation, then start countdown. */
  function showSpawnAlertSequence(
    breachPath: Vec2[],
    destroyCells: Vec2[],
  ): void {
    setPhase(gs, PHASE_CINEMATIC);
    // C1: the tower on the erupting spawn cell (breachPath[0]) is NEVER reached
    // by the path-reveal animation (it advances from index 1) and the cell is
    // already PATH_ENTRY — so destroy + refund it explicitly here at eruption,
    // then hand the animation only the path-blocker towers (index ≥ 1).
    let animDestroyCells = destroyCells;
    const entryCell = breachPath[0];
    if (
      entryCell &&
      destroyCells.some((c) => c.x === entryCell.x && c.y === entryCell.y)
    ) {
      eventBus.emit("breach.destroyCell", { x: entryCell.x, y: entryCell.y });
      eventBus.emit("breach.towerDestroyed", {
        x: entryCell.x,
        y: entryCell.y,
      });
      animDestroyCells = destroyCells.filter(
        (c) => !(c.x === entryCell.x && c.y === entryCell.y),
      );
    }
    ctx.spawnAlert.show(() => {
      // After alert dismissed → animate path reveal with destruction
      eventBus.emit("path.revealStarted");
      ctx.pathRevealAnimation.start(
        breachPath,
        animDestroyCells,
        (cell: Vec2) => {
          // CR 2.1: Destroy the tower at this cell + play SFX
          eventBus.emit("breach.destroyCell", { x: cell.x, y: cell.y });
          eventBus.emit("breach.towerDestroyed", { x: cell.x, y: cell.y });
        },
        () => {
          // After path animation completes → recalculate path, then drain any
          // further queued breach (the pincer's second wall) before the
          // countdown. BL-146: re-enter the pre-wave phase, which shows the next
          // pending alert or, when the queue is empty, starts the countdown.
          ctx.recalculatePath();
          ctx.waveManager.pathPixels = gs.pathPixels;
          enterPreWavePhase();
        },
      );
    });
  }

  /**
   * CR1+CR2: Transition to pre-wave preparation phase.
   * If a breach activated a new spawn this cycle, show its alert first; BL-146
   * drains the breach queue one spawn at a time (the Act III pincer queues ≥2).
   */
  function enterPreWavePhase(): void {
    const next = pendingBreaches.shift();
    if (next) {
      showSpawnAlertSequence(next.path, next.destroyedCells);
    } else {
      startPreWaveCountdown();
    }
  }

  /** Standard post-wave progression: clear stale breach state, run the
   *  next-wave breach check (with cinematic), offer the draft, then enter the
   *  pre-wave countdown. Shared by normal wave completion AND the endless
   *  CONTINUE path so wave 36+ behaves identically to any other transition
   *  (draft offer + breach handling + auto countdown). */
  function beginNextWaveCycle(completedWave: number): void {
    // Reset pending breach data
    pendingBreaches = [];

    const proceedPostWave = (): void => {
      const cards = draftSystem.drawCards(gs.wave, draftSystem.drawCount);

      // Bug 8 (cosmetic): expire duration cards + re-render the deck bar NOW,
      // before the draft is shown, so the freed slot is visible first and the
      // player then drafts into it (removing the "temp card replaced my card"
      // illusion). drawCards already ran against the pre-expiry pool above, so
      // the draw is unchanged — determinism-neutral; the baseline never drafts.
      const expired = draftSystem.expireCards(completedWave + 1);
      for (const card of expired) removeCardEffect(card, ctx);
      if (expired.length > 0) {
        ctx.skillDeckBar.update(
          [...draftSystem.cards],
          draftSystem.maxSlots,
          gs.wave,
        );
      }

      if (cards.length === 0) {
        enterPreWavePhase();
        return;
      }

      setPhase(gs, PHASE_DRAFT);

      const handleSelect = (selected: Card): void => {
        const isInstant = selected.effect.target === "instant";

        // ─── Payload cards: arm in rack, do NOT fire ───────────────
        if (isPayloadCard(selected)) {
          const { payloadRack } = ctx;
          if (payloadRack.isFull) {
            ctx.gameBanner.show(t("payload.rackFull"), COLORS.ORACLE_RED, 1800);
            return;
          }
          payloadRack.arm(selected);
          draftSystem.selectCard(selected);
          // D-331: a status confirmation ("click to deploy"), not an urgent
          // warning — TV, not the grid.
          commsStatus(
            ctx,
            t("payload.armed.banner", {
              name: localizedName("card", selected.id, selected.name),
            }),
            PRIORITY.LOW,
          );
          draftUI.hide();
          ctx.skillDeckBar.update(
            [...draftSystem.cards],
            draftSystem.maxSlots,
            gs.wave,
          );
          enterPreWavePhase();
          return;
        }

        if (draftSystem.isFull && !isInstant) {
          draftUI.hide();
          skillOverview.showSwap(
            selected,
            [...draftSystem.cards],
            gs.wave,
            (slotIndex) => {
              const unloaded = draftSystem.cards[slotIndex];
              if (unloaded) removeCardEffect(unloaded, ctx);
              draftSystem.swapCard(slotIndex, selected);
              applyCardEffect(selected, ctx);
              ctx.skillDeckBar.update(
                [...draftSystem.cards],
                draftSystem.maxSlots,
                gs.wave,
              );
              enterPreWavePhase();
            },
            () => {
              enterPreWavePhase();
            },
          );
          return;
        }
        draftSystem.selectCard(selected);
        if (isInstant) {
          // D-331: a status confirmation, not an urgent warning — TV, not grid.
          commsStatus(ctx, t("payload.instantFired"), PRIORITY.LOW);
        }
        applyCardEffect(selected, ctx);
        draftUI.hide();
        ctx.skillDeckBar.update(
          [...draftSystem.cards],
          draftSystem.maxSlots,
          gs.wave,
        );
        enterPreWavePhase();
      };

      const handleSkip = (): void => {
        economy.addGold(draftSystem.skipReward);
        draftUI.hide();
        enterPreWavePhase();
      };

      draftUI.show(cards, handleSelect, handleSkip);
    };

    const nextWave = completedWave + 1;
    const { breachSystem, breachCinematic } = ctx;
    // P23-6: Boss waves are template-driven, not modulo-based
    const nextTemplate = ctx.waveManager.getWaveTemplate(nextWave);
    const nextIsBoss =
      nextTemplate?.groups.some(
        (g: { type: string }) => g.type === "boss" || g.type === "final_boss",
      ) ?? false;
    // BL-146: play a list of eruptions back-to-back — each gets its own
    // "ERUPTION IMMINENT" telegraph (fairness §6.3 invariant 1), force-reveal,
    // and breach cinematic; their reveal paths queue for the post-wave
    // spawn-alert sequence. When all have played, `onAllDone` proceeds. A single
    // breach is just a one-element list, so the ordinary path is unchanged.
    const runBreachSequence = (
      results: BreachResult[],
      onAllDone: () => void,
    ): void => {
      let index = 0;
      const playNext = (): void => {
        if (index >= results.length) {
          onAllDone();
          return;
        }
        const result = results[index++]!;
        if (
          ctx.spawnDetectionSystem.isHidden(result.spawn.col, result.spawn.row)
        ) {
          // D-331: cinematic narration (PHASE_CINEMATIC — the player can't act
          // on it), synced to the breach cinematic that's about to play. TV,
          // not the grid; MED so it isn't starved by flavour but doesn't
          // outrank an actual boss/breach beat.
          commsStatus(
            ctx,
            t("breach.eruptionImminent", {
              col: result.spawn.col,
              row: result.spawn.row,
            }),
            PRIORITY.MED,
          );
        }
        // Force-reveal if the spawn was hidden (undetected by Signal).
        ctx.spawnDetectionSystem.forceReveal(result.spawn.col, result.spawn.row);
        setPhase(gs, PHASE_CINEMATIC);
        breachCinematic.start(result);
        const unsubBreach = eventBus.on("breach.completed", (data: unknown) => {
          unsubBreach();
          const ev = data as { path: Vec2[]; destroyedCells: Vec2[] };
          pendingBreaches.push({
            path: ev.path,
            destroyedCells: ev.destroyedCells ?? [],
          });
          playNext();
        });
      };
      playNext();
    };

    // BL-146 / D-248 — the Act III converging pincer takes priority: erupt ≥2
    // spawns from different walls this transition.
    if (breachSystem.shouldPincerBreach(nextWave)) {
      const pincer = breachSystem.executePincer(nextWave);
      if (pincer.length > 0) {
        runBreachSequence(pincer, proceedPostWave);
        return;
      }
    }

    const deferredBreach = breachSystem.hasDeferredBreach();
    if (
      deferredBreach ||
      breachSystem.shouldScriptedBreach(nextWave) ||
      breachSystem.shouldAdaptiveBreach(nextWave) ||
      breachSystem.shouldBossBreach(nextWave, nextIsBoss)
    ) {
      const result = breachSystem.executeBreach(nextWave);
      if (result) {
        runBreachSequence([result], proceedPostWave);
        return;
      }
    }

    proceedPostWave();
  }

  unsubs.push(
    eventBus.on("wave.completed", (d) => {
      const completedWave = d?.wave ?? gs.wave;
      setWaveActive(gs, false);
      combatSystem.decrementBossWaveCounter();
      // S195/BL-280 (law #3, one flash per beat): the base 0.2 green flash is
      // retired — the S189 celebration layer (main.ts, feelBudget.waveClear)
      // owns the wave-clear flash at 0.32; two same-frame green flashes were
      // measured double-firing on every clear (S195-F02).
      particleSystem.emit(480, 336, "fire", 20);

      if (state.leaksThisWave === 0) {
        gs.perfectStreak++;
        if (gs.perfectStreak >= 3 && gs.perfectStreak % 3 === 0) {
          // D-331: praise, not a warning — TV, not the grid.
          commsStatus(ctx, t("streak.flawless"), PRIORITY.MED);
        }
      } else {
        gs.perfectStreak = 0;
      }
      state.leaksThisWave = 0;
      // S143 / BL-136: arm the next wave to re-show the CORE BREAKER warning
      // once if a heavy spawns again. One warning per wave, by design.
      state.coreBreakerShownThisWave = false;

      // D-331: routine per-wave STATUS, not a warning — TV, not the grid. LOW
      // so it never outranks a real boss/breach beat still holding the slot.
      commsStatus(ctx, t("wave.processClear"), PRIORITY.LOW);

      if (completedWave === FINAL_WAVE && !gs.isEndlessMode) {
        const livesBonus = ctx.scoreSystem.addLivesBonus(
          gs.lives,
          GAME.INITIAL_HP,
        );
        // Bug 16: remember the campaign integrity bonus so the endless-death
        // game-over screen can show it (the score is frozen on CONTINUE, so
        // addLivesBonus would otherwise report 0 there).
        gs.campaignLivesBonus = livesBonus;
        eventBus.emit("game.won", {
          wave: completedWave,
          score: ctx.scoreSystem.score,
          livesBonus,
        });
        return;
      }

      beginNextWaveCycle(completedWave);
    }),
  );

  unsubs.push(
    eventBus.on("game.continueEndless", () => {
      // Victory CONTINUE → resume into the next wave via the SAME post-wave
      // path as every other wave (breach handling + draft offer + auto
      // pre-wave countdown), not a bare wave-1-style manual start. gs.wave is
      // still FINAL_WAVE here (the win branch returned before any wave-number
      // advance), so the next cycle correctly targets FINAL_WAVE + 1.
      beginNextWaveCycle(gs.wave);
    }),
  );

  return unsubs;
}
